import React, { type JSX } from 'react';
import { CustomModal, CustomTransComponent } from '../../../components';
import { useConfirm } from '@fiji/common/src/hooks';
import { ModalActions } from '../../../components/CustomModal';
import { FormControlLabel, FormHelperText, List, ListItem, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { WIDGET_DESCRIPTION_LIMIT, WIDGET_NAME_LIMIT } from '@fiji/common/src/constants';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { resetCloneWidgetModal, setCloneWidgetPayload } from '@fiji/common/src/features/widgetManagement/widgetSlice';
import { useHandleWidgetListActions } from '../common';
import { useRBAC, useTextFieldError } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import LanguageTextField from 'components/FormElements/LanguageTextField';
import { setWidgetList } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
export const CloneWidgetModal = React.forwardRef((props: any, ref): JSX.Element => {
    const dispatch = useAppDispatch();
    const { hasTextError, makeErrorHandler } = useTextFieldError();
    const [errorMsg, setErrorMsg] = React.useState<any>(null);
    const [selectedWidget, setSlectedWidget] = React.useState<any>({});
    const { modalType } = props;

    const {
        isLoading,
        payload,
        handleCloneModalAction: handleModalAction,
        handleCloneSave: handleSave,
        cloneWidgetError,
    } = useHandleWidgetListActions('cloneWidgetModal', (args: any): void => onClick(args));

    React.useImperativeHandle(
        ref,
        () => ({
            handleModalAction: (action: boolean, widgetData: any): void => {
                handleModalAction(action, widgetData);
                setSlectedWidget(widgetData);
            },
        }),
        []
    );

    const closeModal = (): any => {
        dispatch(setWidgetList({ data: selectedWidget, widgetId: selectedWidget?.id }));
        onCancel();
    };
    const { isVisible, onCancel, onClick, onConfirm } = useConfirm(
        (): Promise<void> => handleSave(modalType, closeModal)
    );

    const permissions = useTypedSelector(selectCurrentPermission);

    const { hasPermission } = useRBAC(permissions);
    const canCreateSystemWidgets = hasPermission('widget-management_create-system-widgets');
    const canCreateUserWidgets = hasPermission('widget-management_view-user-widgets');

    const { name = '', description = '', isSystemWidget = false } = payload ?? {};
    const isNameError = Boolean(errorMsg && !errorMsg.toLowerCase().includes('description'));
    const isDescriptionError = Boolean(errorMsg && errorMsg.toLowerCase().includes('description'));

    const newWidgetModalActions: ModalActions[] = [
        {
            key: '#creationcancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                onCancel();
                resetCloneWidgetModal();
                setErrorMsg(null);
            },
        },
        {
            key: '#creationsave',
            label: <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />,
            variant: 'contained',
            disabled: !name || isLoading || hasTextError,
            handleClick: (): void => {
                onConfirm();
            },
        },
    ];

    React.useEffect(() => {
        const message = cloneWidgetError?.data?.errorMessage?.[0];
        if (message) {
            setErrorMsg(message);
        }
    }, [cloneWidgetError]);

    return (
        <CustomModal
            key="#new!@1313d"
            type="primary"
            isLoading={isLoading}
            actionsDivider
            header={
                modalType === 'save' ? (
                    <CustomTransComponent translationKey={'WIDGETS:SAVE_AS_LABEL'} />
                ) : (
                    <CustomTransComponent translationKey={'WIDGETS:CLONE_WIDGET'} />
                )
            }
            actions={newWidgetModalActions}
            isOpen={isVisible}
            onClose={onCancel}
            data-testid={props['data-testid'] ?? 'clone-widget-modal'}
        >
            <Typography variant="subtitle1" fontWeight={'600'} my={2}>
                <CustomTransComponent translationKey={'WIDGETS:CLONE_MODAL_HEADER'} />
            </Typography>
            <LanguageTextField
                id="clone-name"
                fullWidth
                name="name"
                label={<CustomTransComponent translationKey={'COMMON:NAME'} />}
                inputProps={{ maxLength: WIDGET_NAME_LIMIT }}
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    dispatch(setCloneWidgetPayload({ key: 'name', value: e.target.value }));
                    if (errorMsg) {
                        setErrorMsg(null);
                    }
                }}
                variant="filled"
                className="margin-top-18"
                required
                handleTextError={makeErrorHandler('name')}
            />
            <Stack direction={'row'} justifyContent={'space-between'} px={2}>
                {isNameError ? (
                    <FormHelperText error={isNameError}>{errorMsg}</FormHelperText>
                ) : (
                    <FormHelperText>
                        <CustomTransComponent translationKey={'COMMON:REQUIRED_LABEL'} />
                    </FormHelperText>
                )}
                <FormHelperText>{`${name.length}/${WIDGET_NAME_LIMIT}`}</FormHelperText>
            </Stack>
            <LanguageTextField
                id="outlined-password-input"
                fullWidth
                variant="filled"
                name="description"
                inputProps={{ maxLength: WIDGET_DESCRIPTION_LIMIT }}
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    dispatch(setCloneWidgetPayload({ key: 'description', value: e.target.value }));
                }}
                label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />}
                className="margin-top-18"
                handleTextError={makeErrorHandler('description')}
            />
            <FormHelperText className="text-align-end padding-x-16">
                {isDescriptionError && <FormHelperText error={isDescriptionError}>{errorMsg}</FormHelperText>}
                {`${description?.length}/${WIDGET_DESCRIPTION_LIMIT}`}
            </FormHelperText>

            {canCreateSystemWidgets && canCreateUserWidgets && (
                <>
                    <Typography variant="subtitle1" fontSize={'16px'} fontWeight={'600'} mt={'18px'}>
                        <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:IS_THIS_A_SYSTEM_OR_USER_WIDGET'} />
                    </Typography>
                    <List>
                        <RadioGroup
                            id="creationmodalradio"
                            value={isSystemWidget}
                            onChange={(e: any): any => {
                                dispatch(
                                    setCloneWidgetPayload({
                                        key: 'isSystemWidget',
                                        value: Boolean(e.target.value !== 'false'),
                                    })
                                );
                            }}
                        >
                            <ListItem>
                                <FormControlLabel
                                    value={true}
                                    control={<Radio />}
                                    id="system"
                                    label={
                                        <Typography variant="body1">
                                            <span className="f-16 fw-600 black-500">
                                                <CustomTransComponent
                                                    translationKey={'NEW_WIDGET_MODAL:SYSTEM_WIDGET'}
                                                />{' '}
                                            </span>{' '}
                                            <CustomTransComponent
                                                translationKey={
                                                    'DASHBOARDS:CLONE_MODAL_DASHBOARD.SYSTEM_DASHBOARD_DESCRIPTION'
                                                }
                                            />
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            <ListItem>
                                <FormControlLabel
                                    value={false}
                                    id="user"
                                    control={<Radio />}
                                    label={
                                        <Typography variant="body1">
                                            <span className="f-16 fw-600 black-500">
                                                <CustomTransComponent translationKey={'NEW_WIDGET_MODAL:USER_WIDGET'} />{' '}
                                            </span>{' '}
                                            <CustomTransComponent
                                                translationKey={
                                                    'DASHBOARDS:CLONE_MODAL_DASHBOARD.USER_DASHBOARD_DESCRIPTION'
                                                }
                                            />
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        </RadioGroup>
                    </List>
                </>
            )}
        </CustomModal>
    );
});
